import { useTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import StarIcon from '@mui/icons-material/Star';

const PODIUM_COLORS = {
  0: { bg: '#FFD700', color: '#7a5100', label: '🥇' },
  1: { bg: '#C0C0C0', color: '#4a4a4a', label: '🥈' },
  2: { bg: '#CD7F32', color: '#5c2e00', label: '🥉' },
};

export default function ProvisionalResultsTable({ results = [] }) {
  const { t } = useTranslation();

  const userMatchingWords = results.filter((r) => r.userWord).map((r) => r.word);
  const hasUserMatches = userMatchingWords.length > 0;
  return (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        {t('results.provisionalTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t('results.provisionalSubtitle')}
      </Typography>

      {hasUserMatches && (
        <Alert
          icon={<StarIcon fontSize="inherit" />}
          severity="success"
          sx={{ mb: 2, fontWeight: 600 }}
        >
          {userMatchingWords.length === 1
            ? t('results.yourWordIsTop', { word: userMatchingWords[0] })
            : t('results.yourWordsAreTop', { words: userMatchingWords.join(', ') })}
        </Alert>
      )}

      <TableContainer sx={{ overflowX: 'auto', touchAction: 'pan-x pan-y' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
              <TableCell>#</TableCell>
              <TableCell>{t('results.tableWord')}</TableCell>
              <TableCell align="center">{t('results.tableCount')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((row, idx) => {
              const podium = PODIUM_COLORS[idx];
              return (
                <TableRow
                  key={idx}
                  sx={{
                    bgcolor: row.userWord
                      ? 'success.light' + '33'
                      : podium
                      ? podium.bg + '22'
                      : 'inherit',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    {podium ? (
                      <Typography variant="body1">{podium.label}</Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {row.position}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.userWord ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          fontWeight={700}
                          sx={{ textTransform: 'capitalize', color: 'success.dark' }}
                        >
                          {row.word}
                        </Typography>
                        <Chip
                          label={t('results.yourWordBadge')}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: 'inline-block',
                          bgcolor: 'grey.300',
                          borderRadius: 1,
                          height: 18,
                          width: `${60 + (idx * 13 + row.amount * 3) % 50}px`,
                          filter: 'blur(3px)',
                          verticalAlign: 'middle',
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{row.amount}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
